import { useCallback, useState } from 'react';
import cn from 'clsx';
import { type ShopChipElementId, trackShopChipInteraction } from '@pkgs/components/utils/tracking';
import { useShopFilterRemovalMessage } from '@pkgs/components/hooks/useShopFilterRemovalMessage';
import { useIsLipaPage } from '@pkgs/components/hooks/useIsLipaPage';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useMapStore } from '@pkgs/components/hooks/stores/useMapStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { useShopsByIds } from '@pkgs/components/hooks/queries/useShopsByIds';
import { usePlaceDetails } from '@pkgs/components/hooks/queries/usePlaceDetails';
import { MAXIMUM_MAP_BBOX } from '@pkgs/components/consts/map';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';

import { Popover, PopoverRoot, PopoverTrigger } from '../Popover/Popover';
import ShopIcon from '../../assets/icons/xsmall/Shop.svg';
import CloseIcon from '../../assets/icons/xsmall/Cross.svg';
import ArrowIconSmall from '../../assets/icons/xsmall/Arrow-Thin-Down.svg';

import styles from './LipaChip.module.css';

export const LipaChip = ({ className }: { className?: string }) => {
  const {
    t,
    tracking,
    countryCode,
    options: { language },
  } = useAppContext();
  const { removeShopFilterWithUndoMessage } = useShopFilterRemovalMessage();
  const updateMapBounds = useMapStore((state) => state.updateMapBounds);
  const placeIds = useSearchStore((state) => state.placeIds);
  const { combinedBoundingBox } = usePlaceDetails();
  const { shopNames } = useShopsByIds();

  const isLipaPage = useIsLipaPage();

  const [isOpen, setIsOpen] = useState(false);

  const handleRemove = useCallback(
    (elementId: ShopChipElementId) => {
      if (isLipaPage) {
        removeShopFilterWithUndoMessage({
          elementId,
        });
      }
      if (placeIds?.length && combinedBoundingBox) {
        updateMapBounds(combinedBoundingBox);
      } else {
        updateMapBounds(MAXIMUM_MAP_BBOX);
      }
    },
    [isLipaPage, removeShopFilterWithUndoMessage, updateMapBounds, placeIds, combinedBoundingBox],
  );

  const handleDropdownClick = useCallback(() => {
    if (tracking) {
      trackShopChipInteraction({
        elementId: 'lipa-chip_dropdown',
        tracking: tracking,
        language: language,
        countryCode: countryCode,
      });
    }
  }, [tracking, language, countryCode]);

  return (
    <PopoverRoot open={isOpen} modal="trap-focus" onOpenChange={setIsOpen}>
      <div className={cn(styles.chipContainer, className)}>
        <PopoverTrigger
          render={
            <button className={styles.dropdownButton} onClick={handleDropdownClick} type="button">
              <span className={styles.value}>
                <span className={styles.shopIcon}>
                  <ShopIcon />
                </span>
                {t('search.licencePartner.chip.title', { count: shopNames.length })}
              </span>
              <span className={styles.arrowIconContainer}>
                <ArrowIconSmall className={cn(styles.arrowIconSmall, isOpen && styles.arrowIconSmallOpen)} aria-hidden="true" />
              </span>
            </button>
          }
        />
        <Popover aria-label={t('search.licencePartner.chip.title', { count: shopNames.length })} className={styles.dialog} sideOffset={3}>
          <div className={styles.dialogBody}>
            <Copy text={t('search.licencePartner.chip.label', { count: shopNames.length })} size="l" bold />
            <ul className={styles.shopList}>
              {shopNames.map((shopName) => (
                <li key={shopName}>
                  <Copy className={styles.shopListItemCopy} text={shopName} size="l" />
                </li>
              ))}
            </ul>
            <Button
              className={styles.seeAllButton}
              size="small"
              variant="secondary"
              label={t('search.licensePartner.chip.seeAllButton')}
              onClick={() => handleRemove('lipa-chip_secondary_show-all-properties')}
            />
          </div>
        </Popover>
        <IconButton
          className={styles.removeButton}
          icon={<CloseIcon />}
          label={t('search.licensePartner.chip.seeAllButton')}
          onClick={() => handleRemove('lipa-chip_remove')}
          variant="tertiary"
        />
      </div>
    </PopoverRoot>
  );
};
